import type { DirectiveId } from "@/lib/tool-kit/essay/prompt-analyzer";

/**
 * What each task word means, in plain English, and what it asks the response to do.
 *
 * English defaults for the Essay Prompt Analyzer; the widget exposes them through its
 * strings so a translated page can replace them. Definitions describe the usual
 * academic sense — an instructor's own definition, where one is given, wins.
 */

export const DIRECTIVE_LABELS: Record<DirectiveId, string> = {
  "to-what-extent": "To what extent",
  "how-far": "How far",
  agree: "Agree or disagree",
  "compare-contrast": "Compare and contrast",
  compare: "Compare",
  contrast: "Contrast",
  analyze: "Analyze",
  evaluate: "Evaluate",
  assess: "Assess",
  discuss: "Discuss",
  argue: "Argue",
  explain: "Explain",
  describe: "Describe",
  critique: "Critique",
  critically: "Critically",
  synthesize: "Synthesize",
  justify: "Justify",
  examine: "Examine",
  outline: "Outline",
  summarize: "Summarize",
  illustrate: "Illustrate",
  define: "Define",
  identify: "Identify",
  interpret: "Interpret",
  explore: "Explore",
  review: "Review",
  reflect: "Reflect",
  propose: "Propose",
  recommend: "Recommend",
  defend: "Defend",
  demonstrate: "Demonstrate",
  show: "Show how / that",
  trace: "Trace",
  classify: "Classify",
  distinguish: "Distinguish",
  relate: "Relate",
  "account-for": "Account for",
  "comment-on": "Comment on",
  consider: "Consider",
  state: "State",
  list: "List",
  apply: "Apply",
  predict: "Predict",
  respond: "Respond to",
  choose: "Choose",
  develop: "Develop",
  persuade: "Persuade",
  investigate: "Investigate",
  elaborate: "Elaborate",
  annotate: "Annotate",
  narrate: "Narrate",
  prove: "Prove",
  formulate: "Formulate",
  why: "Why…?",
  how: "How…?",
};

export const DIRECTIVE_DEFINITIONS: Record<DirectiveId, string> = {
  "to-what-extent":
    "A question about degree: how far something is true, not just whether it is.",
  "how-far": "Another question about degree, often paired with “do you agree”.",
  agree: "Asks for your position on a statement or claim.",
  "compare-contrast":
    "Examine similarities and differences together, as one analysis.",
  compare:
    "Set two or more things side by side to find similarities — and usually differences too.",
  contrast: "Focus on the differences between things.",
  analyze: "Break something into its parts and examine how they work together.",
  evaluate: "Judge the value, quality, or effectiveness of something.",
  assess:
    "Weigh how important, successful, or valid something is — close to evaluate.",
  discuss: "Examine an issue from more than one angle.",
  argue: "Take a position and try to convince the reader of it.",
  explain: "Make something clear by giving its causes, reasons, or workings.",
  describe: "Give a detailed account of what something is like.",
  critique:
    "Give a balanced appraisal of a work or argument, strengths and weaknesses alike.",
  critically:
    "A modifier: question the evidence and assumptions instead of accepting them.",
  synthesize: "Combine ideas from several sources into one new whole.",
  justify: "Give good reasons for a decision, claim, or conclusion.",
  examine: "Look at something closely and in detail.",
  outline: "Give the main points or structure without the full detail.",
  summarize: "Restate the main points briefly, in your own words.",
  illustrate: "Make a point clear with examples (or a diagram, if allowed).",
  define: "Give the precise meaning of a term.",
  identify: "Name or point out the relevant items.",
  interpret: "Explain what something means or signifies.",
  explore: "Investigate an open question from several angles.",
  review: "Survey a body of work or a topic and assess it.",
  reflect: "Think about an experience or idea and what it has taught you.",
  propose: "Put forward a plan, solution, or idea for consideration.",
  recommend: "Advise a course of action.",
  defend: "Support a position against criticism.",
  demonstrate: "Show something clearly with evidence or examples.",
  show: "Make a relationship or result evident, step by step.",
  trace: "Follow how something developed over time.",
  classify: "Sort things into groups or categories.",
  distinguish: "Point out the differences between closely related things.",
  relate: "Show how two or more things are connected.",
  "account-for": "Explain why something happened.",
  "comment-on": "Give a reasoned opinion on something, based on evidence.",
  consider: "Think carefully about a factor or view and take it into account.",
  state: "Present something clearly and briefly.",
  list: "Give a series of items, usually without discussion.",
  apply: "Use a concept, theory, or method in a new situation.",
  predict: "Say what is likely to happen, and why.",
  respond: "React to a text, question, or argument.",
  choose: "Select from the options the prompt offers.",
  develop: "Build out an idea, argument, or position.",
  persuade: "Convince the reader to accept a view or take an action.",
  investigate: "Research a question systematically.",
  elaborate: "Add detail, examples, and explanation to a point.",
  annotate: "Add notes to a text or source list that summarize and comment.",
  narrate: "Tell a story or recount events in order.",
  prove: "Establish that something is true through evidence and logic.",
  formulate:
    "Develop something — a question, hypothesis, or position — and state it precisely.",
  why: "A question about causes, reasons, or motives.",
  how: "A question about process, method, or means.",
};

export const DIRECTIVE_TASKS: Record<DirectiveId, string> = {
  "to-what-extent":
    "Take a measured position — fully, largely, partly, barely — and weigh evidence on both sides to justify that degree.",
  "how-far":
    "Say how far you agree and defend that degree with evidence for and against.",
  agree:
    "Choose a side, or a qualified middle ground, state it early, and argue it with evidence.",
  "compare-contrast":
    "Organize around points of comparison and explain what the similarities and differences reveal.",
  compare:
    "Use the same criteria for each subject and say what the comparison shows, not just what it finds.",
  contrast:
    "Show how the subjects differ on specific points and why those differences matter.",
  analyze:
    "Go beyond summary: explain how and why each part contributes to the whole, with evidence.",
  evaluate:
    "Weigh strengths and weaknesses against clear criteria and reach a supported judgment.",
  assess:
    "Decide how significant or effective something is and justify that verdict with evidence.",
  discuss:
    "Present the main perspectives with evidence, then reach a reasoned conclusion of your own.",
  argue:
    "State a clear, debatable thesis, support it with evidence, and answer the strongest objection.",
  explain:
    "Show how or why something happens, step by step, rather than only describing it.",
  describe:
    "Present the main features or events accurately and in a logical order; hold judgment unless asked.",
  critique:
    "Identify strengths and weaknesses, test the evidence and reasoning, and give an overall verdict.",
  critically:
    "Question assumptions, evidence, and methods, and discuss limitations as well as strengths.",
  synthesize:
    "Put the sources in conversation to build your own argument — don't summarize them one at a time.",
  justify:
    "Support each claim with evidence and logic, and show why the alternatives are weaker.",
  examine:
    "Investigate the key aspects carefully and comment on what the details show.",
  outline:
    "Set out the essential points or stages concisely, leaving out minor detail.",
  summarize:
    "Condense the key ideas in your own words, without adding your opinion.",
  illustrate:
    "Back each point with concrete, well-chosen examples that make it clear.",
  define:
    "State the meaning clearly and note how the term is used in your field; cite a source if it is contested.",
  identify:
    "Name the relevant items clearly and briefly — usually a first step before analysis.",
  interpret:
    "Say what the text, data, or evidence means and support your reading with details.",
  explore:
    "Consider several possibilities or perspectives; your conclusion can be tentative.",
  review:
    "Summarize the main contributions and comment on their strengths, weaknesses, and gaps.",
  reflect:
    "Connect your experience to course concepts and explain how your understanding changed.",
  propose:
    "Describe the proposal precisely, show why it is needed, and show that it is feasible.",
  recommend:
    "Give specific, practical recommendations and justify each one with evidence.",
  defend:
    "State the position, answer the main objections, and show why it still holds.",
  demonstrate:
    "Provide evidence or worked examples that make the point hard to dispute.",
  show: "Lay out the evidence and reasoning so the reader can follow every step.",
  trace: "Describe the stages in order and explain how each led to the next.",
  classify:
    "Set out the categories and their criteria, then place each item with a reason.",
  distinguish: "Make the differences precise and explain why they matter.",
  relate:
    "Explain the link — causal, logical, or by similarity — with evidence.",
  "account-for":
    "Give the causes or reasons, not just a description of what happened.",
  "comment-on":
    "Identify the main issues and give a brief, supported view on each.",
  consider:
    "Address the named factor or perspective explicitly in your answer.",
  state:
    "Give the point plainly and concisely; no extended argument is needed.",
  list: "Present the items as a clear, concise list or sequence.",
  apply:
    "Show how the theory works on your case and what it reveals that you would otherwise miss.",
  predict:
    "State the expected outcome and give the reasons or evidence behind it.",
  respond:
    "Engage directly with the source — agree, disagree, or extend it — and support your response.",
  choose: "Make your choice clear early and address only the option you chose.",
  develop:
    "Expand the idea with reasons, evidence, and analysis so it grows rather than repeats.",
  persuade:
    "Use evidence and appeals suited to the audience and end with a clear call or judgment.",
  investigate:
    "Gather and examine evidence, explain how you did it, and report what you found.",
  elaborate:
    "Expand on the point with further examples, reasoning, or context.",
  annotate:
    "For each item, briefly summarize it and comment on its usefulness or reliability.",
  narrate:
    "Present events in a clear sequence with relevant detail, and make their significance clear.",
  prove:
    "Build a chain of evidence and reasoning that leaves no reasonable doubt.",
  formulate:
    "Express your question, hypothesis, or position clearly and specifically.",
  why: "Explain causes or motives, not just what happened, and rank them if you can.",
  how: "Explain the mechanism or method step by step, with evidence.",
};
